import React, { useEffect, useRef, useState, useCallback } from "react";
import { Modal, Button } from "antd";
import { ReactComponent as StarIcon } from "../../../assets/Icons/starIcon.svg";
import "./UserAllocationDetails.scss";

const EMPTY = "—";

const isBlank = (value) =>
  value === null || value === undefined || value === "" || value === "-";

/* The field lists come in as rows of two to five entries, which the old
   markup laid out with justify-content: space-between - so a short row
   spread its two fields to opposite edges of the modal while the row above
   it held five. Flattened into one grid, every field gets the same cell. */
const InfoGroup = ({ title, data }) => (
  <div className="infoGroup">
    {title && <p className="infoGroupTitle">{title}</p>}
    <div className="infoGrid">
      {data.flat().map(({ label, value }, index) => (
        <div className="infoItem" key={`${label}-${index}`}>
          <p className="infoLabel">{label}</p>
          <p className={`infoValue ${isBlank(value) ? "is-empty" : ""}`}>
            {isBlank(value) ? EMPTY : value}
          </p>
        </div>
      ))}
    </div>
  </div>
);

const UserAllocationDetails = ({ open, onClose, record, type }) => {
  const scrollRef = useRef(null);
  const [scrollClass, setScrollClass] = useState("");

  const updateBlur = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollHeight <= clientHeight + 1) {
      setScrollClass("");
    } else if (scrollTop <= 0) {
      setScrollClass("scroll-bottom");
    } else if (scrollTop + clientHeight >= scrollHeight - 1) {
      setScrollClass("scroll-top");
    } else {
      setScrollClass("scroll-middle");
    }
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!open || !container) return;

    updateBlur();
    const timeout = setTimeout(updateBlur, 0);
    container.addEventListener("scroll", updateBlur);

    return () => {
      clearTimeout(timeout);
      container.removeEventListener("scroll", updateBlur);
    };
  }, [open, updateBlur, record]);

  useEffect(() => {
    if (!open && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [open]);

  if (!record) return null;

  const getValue = (key) => record?.[key] ?? "-";

  const personalInfo = [
    [
      { label: "Name", value: getValue("customer_name") },
      { label: "Loan No", value: getValue("loan_no") },
      { label: "Mode Repayment", value: getValue("mode_repayment") },
      { label: "Branch Name", value: getValue("branch_name") },
      { label: "Maturity Date", value: getValue("maturity_date") },
    ],
    [
      { label: "Registration Number", value: getValue("reg_no") },
      { label: "Date of Birth", value: getValue("dob") },
      { label: "Customer Type", value: getValue("cust_type") },
      { label: "Last Due Date", value: getValue("last_due_date") },
      { label: "Marital Status", value: getValue("marital_status") },
      { label: "Occupation", value: getValue("occupation") },
    ],
  ];

  const loanDetails = [
    [
      { label: "Loan No", value: getValue("loan_no") },
      { label: "Loan EMI", value: getValue("loan_emi") },
      { label: "Total Amount Due", value: getValue("total_amount_due") },
      { label: "Bounce Charges", value: getValue("bounce_charges") },
      { label: "Other Charges", value: getValue("other_charges") },
    ],
    [
      { label: "DPD", value: getValue("dpd") },
      { label: "Bucket", value: getValue("bucket") },
      { label: "Disbursal Date", value: getValue("disbursal_date") },
      { label: "Disbursal Amount", value: getValue("disbursal_amount") },
      { label: "Bounce Reason", value: getValue("bounce_reason") },
    ],
    [
      { label: "Bounce Date", value: getValue("bounce_date") },
      { label: "Due Date", value: getValue("due_date") },
      { label: "EMI START DATE", value: getValue("emi_start_date") },
      { label: "Link Account-1", value: getValue("link_acct_1") },
      { label: "Link Account-2", value: getValue("link_acct_2") },
    ],
    [
      { label: "Link Account-3", value: getValue("link_acct_3") },
      { label: "Link Account-4", value: getValue("link_acct_4") },
      { label: "Link Account-5", value: getValue("link_acct_5") },
      { label: "Last Contacted Date", value: getValue("last_contacted_date") },
      { label: "Last Contacted On", value: getValue("last_contacted_on") },
    ],
    [
      { label: "Broken Settlement", value: getValue("broken_settlement") },
      { label: "Settlement Rejected", value: getValue("sett_rejected") },
      {
        label: "Settlement Approved",
        value: getValue("sett_approved_nil_pyt"),
      },
    ],
  ];

  const contactDetails = [
    [
      {
        label: "Customer Mailing Address-1",
        value: getValue("cust_mailing_addr1"),
      },
      {
        label: "Customer Mailing Address-2",
        value: getValue("cust_mailing_addr2"),
      },
      {
        label: "Customer Mailing Address-3",
        value: getValue("cust_mailing_addr3"),
      },
      {
        label: "Customer Mailing Address-4",
        value: getValue("cust_mailing_addr4"),
      },
    ],
    [
      { label: "Customer Mailing City", value: getValue("cust_mailing_city") },
      { label: "Phone Number-2", value: getValue("cust_mailing_phone2") },
      { label: "Mobile Number", value: getValue("cust_mobile_no") },
      { label: "Last Due Date", value: getValue("last_due_date") },
    ],
  ];

  const unallocatedDetails = [
    [
      { label: "Name", value: getValue("customer_name") },
      { label: "Loan No", value: getValue("loan_no") },
      { label: "Mobile", value: getValue("cust_mobile_no") },
      { label: "DPD", value: getValue("dpd") },
    ],
    [
      { label: "Total Due", value: getValue("total_amount_due") },
      { label: "Branch", value: getValue("branch_name") },
      { label: "Due Date", value: getValue("due_date") },
    ],
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={790}
      title={
        /* which case this is, rather than a bare "All Details" */
        <div className="detailsTitle">
          <span className="detailsTitle__main">All Details</span>
          {!isBlank(record?.customer_name) && (
            <span className="detailsTitle__sub">
              {record.customer_name}
              {!isBlank(record?.loan_no) && (
                <>
                  <i className="dot" />
                  {record.loan_no}
                </>
              )}
            </span>
          )}
        </div>
      }
      className={`userAllocationDetailsModal ${scrollClass}`}
    >
      {/* the fades sit in the same box as the scroller, so they no longer
          depend on hardcoded offsets from the modal frame */}
      <div className="modalScrollShell">
        <div className="blurTop" />
        <div className="blurBottom" />
        <div className="modalScrollWrapper" ref={scrollRef}>
          {type === "unallocated" ? (
            <InfoGroup title="Basic Details" data={unallocatedDetails} />
          ) : (
            <>
              <InfoGroup title="Personal Information" data={personalInfo} />
              <InfoGroup title="Loan Details" data={loanDetails} />
              <InfoGroup title="Contact Details" data={contactDetails} />
            </>
          )}
        </div>
      </div>

      <div className="footerActions">
        <Button
          icon={
            <span className="starIcon">
              <StarIcon />
            </span>
          }
          className="bookmarkBtn"
        />
        <Button type="primary" className="allocateBtn">
          Allocate
        </Button>
      </div>
    </Modal>
  );
};

export default UserAllocationDetails;
